import logException from '../services/logException';
import postMessageToFrame from '../utils/postMessageToFrame';
import { formatCentsToDecimals } from '../services/formatPrice';

export default function eventTracker(
  event,
  properties = {},
  pageId,
  { pageslug, sellerUrlId, iframeId } = {},
) {
  const eventName = `checkoutpage-${event}`;

  const { dataLayer, ga, fbq, gtag } = window;

  // Facebook Pixel
  if (fbq && typeof fbq === 'function') {
    try {
      if (event === 'checkout') {
        const { amount, currency, orderId, customerEmail, coupon } = properties;
        const value = formatCentsToDecimals(amount, currency);

        fbq(
          'track',
          'Purchase',
          {
            value,
            currency,
            page: pageId,
            checkout: pageId,
            orderId: orderId,
            customerEmail: customerEmail,
            coupon: coupon,
          },
          {
            eventID: orderId,
          },
        );
      } else if (event === 'submission') {
        fbq('track', 'Lead');
      } else {
        fbq('trackCustom', eventName, {
          ...properties,
          page: pageId,
          checkout: pageId,
        });
      }
    } catch (err) {
      logException('eventTracker.fbq', err);
    }
  }

  // Google Tag Manager (dataLayer)
  if (dataLayer && typeof dataLayer.push === 'function') {
    try {
      if (event === 'checkout') {
        const { amount, currency, orderId, customerEmail, coupon } = properties;
        const value = formatCentsToDecimals(amount, currency);

        dataLayer.push({
          event: eventName,
          page: pageId,
          checkout: pageId,
          value: value,
          currency: currency,
          orderId: orderId,
          customerEmail: customerEmail,
          coupon: coupon,
        });
      } else {
        dataLayer.push({
          event: eventName,
          page: pageId,
          checkout: pageId,
        });
      }
    } catch (err) {
      logException('eventTracker.dataLayer', err);
    }
  }

  // gtag.js (GA4/GTM/UA)
  if (gtag && typeof gtag === 'function') {
    try {
      if (event === 'checkout') {
        const { amount, currency, orderId, customerEmail, coupon, items } = properties;
        const value = formatCentsToDecimals(amount, currency);

        gtag('event', `checkoutpage-${pageId}`, {
          event_category: event,
          value: value,
          currency: currency,
          orderId: orderId,
          customerEmail: customerEmail,
          coupon: coupon,
        });

        // track purchase event
        gtag('event', 'purchase', {
          value: value,
          transaction_id: orderId,
          currency: currency,
          coupon: coupon,
          items: items.map(item => ({
            item_id: item.sku || item.id,
            item_name: item.name,
            price: formatCentsToDecimals(item.price, currency),
          })),
        });
      } else if (event === 'submission') {
        gtag('event', 'generate_lead');
      } else {
        gtag('event', `checkoutpage-${pageId}`, {
          event_category: event,
        });
      }
    } catch (err) {
      logException('eventTracker.gtag', err);
    }
  }

  // Google Analytics
  if (ga && typeof ga === 'function') {
    try {
      ga('send', 'event', `checkoutpage-${pageId}`, event);
    } catch (err) {
      logException('eventTracker.ga', err);
    }
  }

  // Send event to iframe
  if (event === 'checkout') {
    const { amount, currency, orderId, customerEmail, items, coupon } = properties;
    const value = formatCentsToDecimals(amount, currency);

    const payload = {
      event,
      value,
      currency,
      checkoutId: pageId,
      orderId,
      customerEmail: customerEmail,
      coupon: coupon,
      items: items.map(item => ({
        item_id: item.sku || item.id,
        item_name: item.name,
        price: formatCentsToDecimals(item.price, currency),
      })),
    };

    postMessageToFrame({
      seller: sellerUrlId,
      checkout: pageslug,
      iframeId: iframeId,
      type: `checkoutpage.event`,
      payload,
    });
  } else if (event === 'submission') {
    const { customerEmail } = properties;

    const payload = {
      event,
      pageId,
      customerEmail,
    };

    postMessageToFrame({
      seller: sellerUrlId,
      checkout: pageslug,
      iframeId: iframeId,
      type: `checkoutpage.event`,
      payload,
    });
  } else {
    postMessageToFrame({
      seller: sellerUrlId,
      checkout: pageslug,
      iframeId: iframeId,
      type: `checkoutpage.event`,
      payload: { event, checkoutId: pageId },
    });
  }
}
